import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

interface HeroProps {
  onNavigate: (page: string) => void;
}

const Hero: React.FC<HeroProps> = ({ onNavigate }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Browsers often require 'muted' to be set programmatically 
      // as well as in the attribute for reliable autoplay
      video.muted = true;
      video.defaultMuted = true;
      
      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // If autoplay fails, we just let it show the poster 
          // rather than showing any play buttons or UI.
          console.warn("Ambient video autoplay prevented:", error);
        });
      }
    }
  }, []);

  const auroraStyle: React.CSSProperties = {
    backgroundImage: 'linear-gradient(to right, hsla(228, 51%, 70%, 1) 0%, hsla(27, 90%, 68%, 1) 45%, hsla(29, 100%, 49%, 1) 50%, hsla(27, 90%, 68%, 1) 55%, hsla(228, 51%, 70%, 1) 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  };

  const ribbonItem = (
    <div className="flex items-center gap-8 md:gap-16 px-4">
        <span className="flex items-center gap-2 text-white/90 uppercase tracking-widest">
            GET AN OPENING SLOT FOR ROD WAVE <span className="text-primary font-bold">"DON'T LOOK DOWN TOUR"</span>
        </span>
        <span className="text-slate-500 text-[10px]">//</span>
    </div>
  );

  return (
    <section className="relative w-full h-[85vh] md:h-[90vh] overflow-hidden bg-black mb-16">
       {/* Ambient Background Video */}
       <div className="absolute inset-0 z-0 bg-black pointer-events-none select-none">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            disablePictureInPicture
            disableRemotePlayback
            preload="auto"
            poster="https://i.imgur.com/590xPCo.png"
            className="w-full h-full object-cover opacity-60"
            style={{ filter: 'brightness(0.8)' }}
          >
             <source src="https://i.imgur.com/fPOdC2y.mp4" type="video/mp4" />
          </video>
          {/* Gradients for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/90 z-10"></div>
          {/* Noise Overlay */}
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 z-10 pointer-events-none"></div>
       </div>

        {/* Content Container */}
       <div className="relative z-20 h-full flex flex-col pt-8 pb-12 px-4 sm:px-6 md:px-12 max-w-[1920px] mx-auto">
          
          {/* TOP SECTION: Ribbon (Marquee & Smaller) */}
          <div className="w-full mb-6 md:mb-8 flex justify-center">
             <div className="border-y border-white/10 bg-black/40 backdrop-blur-md overflow-hidden py-1.5 md:py-2 relative w-full max-w-3xl">
                {/* Marquee Wrapper */}
                <div className="flex animate-marquee w-max whitespace-nowrap text-[9px] md:text-[10px] font-mono font-bold tracking-[0.2em] uppercase">
                   {ribbonItem}
                   {ribbonItem}
                   {ribbonItem}
                   {ribbonItem}
                   {ribbonItem}
                   {ribbonItem}
                </div>
                 {/* Fades */}
                <div className="absolute left-0 top-0 bottom-0 w-4 md:w-8 bg-gradient-to-r from-black/60 to-transparent z-10"></div>
                <div className="absolute right-0 top-0 bottom-0 w-4 md:w-8 bg-gradient-to-l from-black/60 to-transparent z-10"></div>
             </div>
          </div>

          {/* MAIN CONTENT AREA */}
          <div className="flex flex-col flex-grow w-full">
             
             {/* HEADLINES GROUP */}
             <div className="w-full text-left mt-2 sm:mt-4 md:mt-8">
                <h1 
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-display tracking-tighter animate-aurora leading-[1.1] flex flex-col gap-0.5 sm:gap-1 md:gap-2"
                    style={auroraStyle}
                >
                    <span className="uppercase">Your Path</span>
                    <span className="uppercase">to</span>
                    <span className="uppercase">spotlight</span>
                </h1>
             </div>

             {/* BUTTON - Pushed to bottom and Bigger */}
             <div className="w-full flex justify-center mt-auto pb-4 sm:pb-8 md:pb-12">
                 <button 
                  onClick={() => onNavigate('submission')}
                  className="bg-primary hover:bg-orange-600 text-white font-mono font-bold py-4 px-8 md:py-6 md:px-16 flex items-center gap-4 transition-all group rounded-sm shadow-[0_0_20px_rgba(249,115,22,0.4)] hover:shadow-[0_0_40px_rgba(249,115,22,0.6)] text-sm md:text-xl tracking-widest uppercase hover:-translate-y-1"
                 >
                  <span>GET A SLOT</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                </button>
             </div>

          </div>

       </div>
    </section>
  );
};

export default Hero;